import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Stage, Layer, Rect, Transformer, Image as KonvaImage, Group,
} from "react-konva";
import { Button, Spin } from "antd";
import {
  PlusOutlined, MinusOutlined, ExpandOutlined,
} from "@ant-design/icons";
import { resolvePetrifilmEntry } from "./utils";
import "./AnnotateCanvasStage.css";

const MIN_BOX_SIZE = 10;
/** Max zoom relative to fit-to-view: 5 => UI shows 500% */
const MAX_ZOOM_RATIO = 5;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const getFitTransform = (width, height, imageSize) => {
  const fitScale = Math.min(width / imageSize, height / imageSize);
  const nextScale = Number.isFinite(fitScale) && fitScale > 0 ? fitScale : 1;
  return {
    scale: nextScale,
    x: (width - (imageSize * nextScale)) / 2,
    y: (height - (imageSize * nextScale)) / 2,
  };
};

const AnnotateCanvasStage = forwardRef(({
  imageSize,
  imageRef,
  isImageLoading,
  roiImageName,
  boxes,
  setBoxes,
  selectedId,
  setSelectedId,
  labels,
  petrifilmColorConfig,
  readOnly = false,
}, ref) => {
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  const viewGroupRef = useRef(null);
  const transformerRef = useRef(null);
  const shapeRefs = useRef({});
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const [panEnabled, setPanEnabled] = useState(true);
  const [transform, setTransform] = useState({
    x: 0,
    y: 0,
    scale: 1,
  });

  const fitTransform = useMemo(() => {
    if (!stageSize.width || !stageSize.height) {
      return null;
    }
    return getFitTransform(stageSize.width, stageSize.height, imageSize);
  }, [imageSize, stageSize.height, stageSize.width]);

  const fitScale = fitTransform?.scale ?? 1;

  const zoomPercent = useMemo(() => {
    if (!fitTransform) return 100;
    const ratio = (transform.scale || 1) / (fitScale || 1);
    return Math.round(ratio * 100);
  }, [fitScale, fitTransform, transform.scale]);

  const maxZoomScale = fitScale * MAX_ZOOM_RATIO;

  const zoomOutDisabled = isImageLoading
    || !fitTransform
    || transform.scale <= fitScale + 1e-6;

  const zoomInDisabled = isImageLoading
    || !fitTransform
    || transform.scale >= maxZoomScale - 1e-6;

  const getBoxStrokeColor = useCallback((box, index) => {
    if (box.customColor) {
      return box.customColor;
    }
    const labelValue = labels[index];
    const raw = petrifilmColorConfig[labelValue] ?? petrifilmColorConfig[String(labelValue)];
    return resolvePetrifilmEntry(raw, "").color;
  }, [labels, petrifilmColorConfig]);

  useEffect(() => {
    const updateStageSize = () => {
      if (!containerRef.current) {
        return;
      }
      setStageSize({
        width: containerRef.current.clientWidth,
        height: containerRef.current.clientHeight,
      });
    };

    updateStageSize();
    window.addEventListener("resize", updateStageSize);
    return () => window.removeEventListener("resize", updateStageSize);
  }, []);

  useLayoutEffect(() => {
    if (!fitTransform) {
      return;
    }
    setTransform(fitTransform);
  }, [fitTransform]);

  useEffect(() => {
    if (readOnly) {
      if (transformerRef.current) {
        transformerRef.current.nodes([]);
      }
      return;
    }

    if (!selectedId || !transformerRef.current) {
      if (transformerRef.current) {
        transformerRef.current.nodes([]);
      }
      return;
    }
    const selectedNode = shapeRefs.current[selectedId];
    if (selectedNode) {
      transformerRef.current.nodes([selectedNode]);
      transformerRef.current.getLayer().batchDraw();
    }
  }, [selectedId, boxes, readOnly]);

  const updateBox = useCallback((id, updates) => {
    setBoxes((prev) => prev.map((box) => (box.id === id ? { ...box, ...updates } : box)));
  }, [setBoxes]);

  const handleStageMouseDown = useCallback((event) => {
    if (readOnly || isImageLoading) {
      return;
    }
    const stage = event.target.getStage();
    const clickedName = event.target.name();

    if (clickedName === "annotation-box") {
      return;
    }

    if (clickedName === roiImageName) {
      setSelectedId(null);
      return;
    }

    if (event.target === stage) {
      setSelectedId(null);
    }
  }, [isImageLoading, readOnly, roiImageName, setSelectedId]);

  const handleStageMouseUp = useCallback(() => {
    setPanEnabled(true);
  }, []);

  const orderedBoxes = useMemo(() => {
    if (!selectedId) return boxes;
    // Render selected box last (top-most) so it captures drag events even when boxes overlap.
    const selected = [];
    const rest = [];
    boxes.forEach((b) => (b.id === selectedId ? selected.push(b) : rest.push(b)));
    return rest.concat(selected);
  }, [boxes, selectedId]);

  const handleBoxDragStart = useCallback((event) => {
    event.cancelBubble = true;
    setPanEnabled(false);
  }, []);

  const handleDragEnd = useCallback((event, box) => {
    updateBox(box.id, {
      x: clamp(event.target.x(), 0, imageSize - box.width),
      y: clamp(event.target.y(), 0, imageSize - box.height),
    });
  }, [imageSize, updateBox]);

  const handleViewGroupDragEnd = useCallback((event) => {
    if (event.target.name() === "annotation-box") {
      return;
    }
    const group = viewGroupRef.current;
    if (!group) {
      return;
    }
    setTransform((prev) => ({
      ...prev,
      x: group.x(),
      y: group.y(),
    }));
  }, []);

  const handleTransformEnd = useCallback((box) => {
    const node = shapeRefs.current[box.id];
    if (!node) {
      return;
    }

    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    node.scaleX(1);
    node.scaleY(1);

    const nextWidth = clamp(node.width() * scaleX, MIN_BOX_SIZE, imageSize);
    const nextHeight = clamp(node.height() * scaleY, MIN_BOX_SIZE, imageSize);
    const nextX = clamp(node.x(), 0, imageSize - nextWidth);
    const nextY = clamp(node.y(), 0, imageSize - nextHeight);

    updateBox(box.id, {
      x: nextX,
      y: nextY,
      width: nextWidth,
      height: nextHeight,
    });
  }, [imageSize, updateBox]);

  const zoom = useCallback((factor) => {
    setTransform((prev) => {
      const minS = fitScale;
      const maxS = fitScale * MAX_ZOOM_RATIO;
      return {
        ...prev,
        scale: clamp(prev.scale * factor, minS, maxS),
      };
    });
  }, [fitScale]);

  const zoomAtPoint = useCallback((pointer, factor) => {
    if (!pointer || !fitTransform || isImageLoading) {
      return;
    }

    setTransform((prev) => {
      const minS = fitScale;
      const maxS = fitScale * MAX_ZOOM_RATIO;
      const oldScale = prev.scale;
      const newScale = clamp(oldScale * factor, minS, maxS);
      if (newScale === oldScale) {
        return prev;
      }

      const anchor = {
        x: (pointer.x - prev.x) / oldScale,
        y: (pointer.y - prev.y) / oldScale,
      };

      return {
        scale: newScale,
        x: pointer.x - anchor.x * newScale,
        y: pointer.y - anchor.y * newScale,
      };
    });
  }, [fitScale, fitTransform, isImageLoading]);

  useEffect(() => {
    const stage = stageRef.current;
    const el = stage?.container?.();
    if (!el) {
      return undefined;
    }

    const onWheel = (evt) => {
      evt.preventDefault();
      const rect = el.getBoundingClientRect();
      const pointer = {
        x: evt.clientX - rect.left,
        y: evt.clientY - rect.top,
      };
      const { deltaY } = evt;
      if (!deltaY) {
        return;
      }
      const scaleBy = 1.06;
      const factor = deltaY > 0 ? 1 / scaleBy : scaleBy;
      zoomAtPoint(pointer, factor);
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomAtPoint, stageSize.height, stageSize.width]);

  const resetView = useCallback(() => {
    if (!fitTransform) {
      return;
    }
    setTransform(fitTransform);
  }, [fitTransform]);

  const getPointerInImageCoords = useCallback((stage) => {
    const pointer = stage?.getPointerPosition?.();
    if (!pointer) return null;
    const scale = transform.scale || 1;
    return {
      x: (pointer.x - transform.x) / scale,
      y: (pointer.y - transform.y) / scale,
    };
  }, [transform.scale, transform.x, transform.y]);

  const handleSelectAtPointer = useCallback((event) => {
    if (readOnly || isImageLoading) return;
    event.cancelBubble = true;

    const stage = event.target?.getStage?.();
    const point = getPointerInImageCoords(stage);
    if (!point) return;

    const hitBoxes = boxes.filter((box) => (
      point.x >= box.x
      && point.x <= box.x + box.width
      && point.y >= box.y
      && point.y <= box.y + box.height
    ));

    if (!hitBoxes.length) return;
    if (hitBoxes.length === 1) {
      setSelectedId(hitBoxes[0].id);
      return;
    }

    const currentIndex = hitBoxes.findIndex((box) => box.id === selectedId);
    const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % hitBoxes.length;
    setSelectedId(hitBoxes[nextIndex].id);
  }, [boxes, getPointerInImageCoords, isImageLoading, readOnly, selectedId, setSelectedId]);

  useImperativeHandle(ref, () => ({
    getViewportCenterInImageCoords: () => {
      const stageCenterX = stageSize.width / 2;
      const stageCenterY = stageSize.height / 2;
      const scale = transform.scale || 1;
      return {
        x: (stageCenterX - transform.x) / scale,
        y: (stageCenterY - transform.y) / scale,
      };
    },
  }), [stageSize.height, stageSize.width, transform.scale, transform.x, transform.y]);

  return (
    <div className="AnnotateImageModal__CanvasArea" ref={containerRef}>
      {stageSize.width > 0 && stageSize.height > 0 && (
        <Stage
          ref={stageRef}
          width={stageSize.width}
          height={stageSize.height}
          onMouseDown={handleStageMouseDown}
          onMouseUp={handleStageMouseUp}
          onTouchEnd={handleStageMouseUp}
        >
          <Layer>
            <Group
              ref={viewGroupRef}
              x={transform.x}
              y={transform.y}
              scaleX={transform.scale}
              scaleY={transform.scale}
              draggable={!readOnly && !isImageLoading && panEnabled}
              onDragEnd={handleViewGroupDragEnd}
            >
              <Rect
                x={0}
                y={0}
                width={imageSize}
                height={imageSize}
                fill="#111"
                name="annotatable-workspace-bg"
              />
              {imageRef.current && (
                <KonvaImage
                  x={0}
                  y={0}
                  width={imageSize}
                  height={imageSize}
                  image={imageRef.current}
                  name={roiImageName}
                />
              )}

              {!isImageLoading && imageRef.current && orderedBoxes.map((box, index) => (
                <Rect
                  key={box.id}
                  ref={(node) => {
                    shapeRefs.current[box.id] = node;
                  }}
                  x={box.x}
                  y={box.y}
                  width={box.width}
                  height={box.height}
                  stroke={getBoxStrokeColor(box, index)}
                  strokeWidth={2}
                  draggable={!readOnly && !isImageLoading && selectedId === box.id}
                  name="annotation-box"
                  onDragStart={handleBoxDragStart}
                  onClick={handleSelectAtPointer}
                  onTap={handleSelectAtPointer}
                  onDragEnd={(event) => handleDragEnd(event, box)}
                  dragBoundFunc={(pos) => ({
                    x: clamp(pos.x, 0, imageSize - box.width),
                    y: clamp(pos.y, 0, imageSize - box.height),
                  })}
                  onTransformEnd={() => handleTransformEnd(box)}
                />
              ))}
              {!readOnly && !isImageLoading && imageRef.current && (
                <Transformer
                  ref={transformerRef}
                  rotateEnabled={false}
                  flipEnabled={false}
                  keepRatio={false}
                  boundBoxFunc={(oldBox, newBox) => {
                    if (newBox.width < MIN_BOX_SIZE || newBox.height < MIN_BOX_SIZE) {
                      return oldBox;
                    }
                    return newBox;
                  }}
                />
              )}
            </Group>
          </Layer>
        </Stage>
      )}

      <div className="AnnotateImageModal__CanvasControls">
        <Button icon={<PlusOutlined />} onClick={() => zoom(1.2)} disabled={zoomInDisabled} />
        <Button
          icon={<MinusOutlined />}
          onClick={() => zoom(1 / 1.2)}
          disabled={zoomOutDisabled}
        />
        <Button icon={<ExpandOutlined />} onClick={resetView} disabled={isImageLoading} />
        {fitTransform && (
          <div className="AnnotateImageModal__CanvasZoomLabel" aria-live="polite">
            {`${zoomPercent}%`}
          </div>
        )}
      </div>
      {isImageLoading && (
        <div className="AnnotateImageModal__CanvasLoadingOverlay">
          <Spin size="large" />
        </div>
      )}
    </div>
  );
});

export default AnnotateCanvasStage;
